import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Eye, Undo2 } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EmptyState } from '@/components/ui/empty-state';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/loading-state';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { usePermission } from '@/components/rbac/use-permission';
import { useStandort } from '@/components/layouts/use-standort';
import { P } from '@/lib/rbac/permissions.generated';
import { formatDate } from '@/lib/dateFormat';
import {
  getKuendigungHistory,
  listKuendigungen,
  withdrawKuendigung,
  REASON_LABELS,
  STATUS_LABELS,
  type KuendigungDTO,
  type KuendigungHistoryEntry,
} from '@/lib/kuendigungen';

const AKTION_TEXT: Record<string, string> = {
  created: 'Angelegt',
  confirmed: 'Bestätigt',
  withdrawn: 'Zurückgezogen',
  effective: 'Wirksam geworden',
  email_sent: 'Bestätigung verschickt',
  updated: 'Geändert',
};

const STATUS_FARBE: Record<string, string> = {
  draft: 'border-slate-300 text-slate-600',
  confirmed: 'border-amber-300 text-amber-800',
  effective: 'border-emerald-300 text-emerald-700',
  withdrawn: 'border-slate-300 text-slate-500',
};

/**
 * Kündigungen im Überblick (T-0075).
 *
 * Liste, Detail und Zurückziehen gab es im Backend längst, aber keine Seite: Eine
 * Kündigung fand man nur über die Person. Gefiltert wird hier im Browser — der
 * Statusfilter des Servers vergleicht mit dem rohen Datenbankwert.
 */
export default function KuendigungenPage() {
  const { selectedStandortId } = useStandort();
  const darfZurueckziehen = usePermission(P.anmeldung.eintrag_bearbeiten);
  const [eintraege, setEintraege] = useState<KuendigungDTO[]>([]);
  const [laedt, setLaedt] = useState(true);
  const [status, setStatus] = useState('offen');
  const [suche, setSuche] = useState('');
  const [zurueckziehen, setZurueckziehen] = useState<KuendigungDTO | null>(null);
  const [arbeitet, setArbeitet] = useState(false);
  // Detailansicht mit Historie (T-0174).
  const [detail, setDetail] = useState<KuendigungDTO | null>(null);
  const [historie, setHistorie] = useState<KuendigungHistoryEntry[] | null>(null);

  const oeffneDetail = async (eintrag: KuendigungDTO) => {
    setDetail(eintrag);
    setHistorie(null);
    try {
      setHistorie(await getKuendigungHistory(eintrag.id));
    } catch {
      setHistorie([]);
    }
  };

  const laden = useCallback(async () => {
    if (!selectedStandortId) {
      setEintraege([]);
      setLaedt(false);
      return;
    }
    setLaedt(true);
    try {
      setEintraege(await listKuendigungen(selectedStandortId));
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Kündigungen konnten nicht geladen werden');
      setEintraege([]);
    } finally {
      setLaedt(false);
    }
  }, [selectedStandortId]);

  useEffect(() => {
    void laden();
  }, [laden]);

  const sichtbar = useMemo(() => {
    const text = suche.trim().toLowerCase();
    return eintraege.filter((eintrag) => {
      if (status === 'offen' && eintrag.status !== 'confirmed' && eintrag.status !== 'draft') return false;
      if (status !== 'offen' && status !== 'alle' && eintrag.status !== status) return false;
      if (!text) return true;
      return [
        eintrag.studentName,
        eintrag.studentEmail,
        eintrag.kontoinhaberName,
        eintrag.kontoinhaberEmail,
        eintrag.kursName,
        eintrag.reasonText,
      ]
        .some((wert) => String(wert || '').toLowerCase().includes(text));
    });
  }, [eintraege, status, suche]);

  const bestaetigeZurueckziehen = async () => {
    if (!zurueckziehen) return;
    setArbeitet(true);
    try {
      await withdrawKuendigung(zurueckziehen.id);
      toast.success('Kündigung zurückgezogen');
      setZurueckziehen(null);
      await laden();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Zurückziehen fehlgeschlagen');
    } finally {
      setArbeitet(false);
    }
  };

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold leading-7">Kündigungen</h1>
        <p className="text-muted-foreground">
          Alle Kündigungen am gewählten Standort. „Offen“ sind die, die noch nicht wirksam sind — sie
          lassen sich bis dahin zurückziehen.
        </p>
      </div>

      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" aria-label="Status filtern">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="offen">Offen</SelectItem>
            <SelectItem value="effective">Wirksam</SelectItem>
            <SelectItem value="withdrawn">Zurückgezogen</SelectItem>
            <SelectItem value="alle">Alle</SelectItem>
          </SelectContent>
        </Select>
        <Input
          className="md:w-72"
          placeholder="Name, E-Mail, Kurs …"
          value={suche}
          onChange={(event) => setSuche(event.target.value)}
        />
      </div>

      {!selectedStandortId ? (
        <EmptyState message="Bitte oben einen Standort wählen." />
      ) : laedt ? (
        <LoadingState message="Kündigungen werden geladen …" />
      ) : sichtbar.length === 0 ? (
        <EmptyState message="Keine Kündigungen in dieser Ansicht." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lernende/r</TableHead>
                <TableHead>Kontoinhaber/in</TableHead>
                <TableHead>Kurs</TableHead>
                <TableHead>Eingegangen</TableHead>
                <TableHead>Wirksam zum</TableHead>
                <TableHead>Grund</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Aktionen</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sichtbar.map((eintrag) => (
                <TableRow key={eintrag.id}>
                  <TableCell>
                    {eintrag.wpUserId ? (
                      <Link className="font-medium hover:underline" to={`/dashboard/people/${eintrag.wpUserId}`}>
                        {eintrag.studentName || 'Ohne Namen'}
                      </Link>
                    ) : (
                      <div className="font-medium">{eintrag.studentName || 'Ohne Namen'}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <div>{eintrag.kontoinhaberName || '—'}</div>
                    {eintrag.kontoinhaberEmail || eintrag.studentEmail ? (
                      <div className="text-xs text-muted-foreground">
                        {eintrag.kontoinhaberEmail || eintrag.studentEmail}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>{eintrag.kursName || `Kurs #${eintrag.kursId}`}</TableCell>
                  <TableCell>
                    {formatDate(eintrag.requestedAt)}
                    <div className="text-xs text-muted-foreground">
                      {eintrag.initiatedBy === 'customer' ? 'vom Kunden' : 'vom Büro'}
                    </div>
                  </TableCell>
                  <TableCell>{formatDate(eintrag.effectiveDate)}</TableCell>
                  <TableCell className="max-w-[16rem]">
                    <div>{eintrag.reasonCategory ? REASON_LABELS[eintrag.reasonCategory] ?? eintrag.reasonCategory : '—'}</div>
                    {eintrag.reasonText ? (
                      <div className="truncate text-xs text-muted-foreground" title={eintrag.reasonText}>
                        {eintrag.reasonText}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={STATUS_FARBE[eintrag.status] ?? ''}>
                      {STATUS_LABELS[eintrag.status] ?? eintrag.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button variant="ghost" size="sm" onClick={() => void oeffneDetail(eintrag)}>
                      <Eye className="mr-1 h-4 w-4" /> Ansehen
                    </Button>
                    {darfZurueckziehen && eintrag.status === 'confirmed' ? (
                      <Button variant="ghost" size="sm" onClick={() => setZurueckziehen(eintrag)}>
                        <Undo2 className="mr-1 h-4 w-4" /> Zurückziehen
                      </Button>
                    ) : null}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <Dialog open={detail !== null} onOpenChange={(offen) => (!offen ? setDetail(null) : undefined)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Kündigung · {detail?.studentName || 'Ohne Namen'}</DialogTitle>
            <DialogDescription>
              {[detail?.kursName, detail?.standortName].filter(Boolean).join(' · ')}
            </DialogDescription>
          </DialogHeader>
          {detail ? (
            <div className="space-y-4 text-sm">
              <dl className="grid grid-cols-[10rem_1fr] gap-x-3 gap-y-1">
                <dt className="text-muted-foreground">Kontoinhaber/in</dt>
                <dd>
                  {detail.kontoinhaberName || '—'}
                  {detail.kontoinhaberEmail ? ` · ${detail.kontoinhaberEmail}` : ''}
                </dd>
                <dt className="text-muted-foreground">Status</dt>
                <dd>{STATUS_LABELS[detail.status] ?? detail.status}</dd>
                <dt className="text-muted-foreground">Eingegangen</dt>
                <dd>
                  {formatDate(detail.requestedAt)} · {detail.initiatedBy === 'customer' ? 'vom Kunden' : 'vom Büro'}
                </dd>
                <dt className="text-muted-foreground">Wirksam zum</dt>
                <dd>{formatDate(detail.effectiveDate)}</dd>
                <dt className="text-muted-foreground">Grund</dt>
                <dd>
                  {detail.reasonCategory ? REASON_LABELS[detail.reasonCategory] ?? detail.reasonCategory : '—'}
                  {detail.reasonText ? <div className="text-muted-foreground">{detail.reasonText}</div> : null}
                </dd>
              </dl>
              <div>
                <h3 className="mb-2 font-medium">Historie</h3>
                {historie === null ? (
                  <p className="text-muted-foreground">Wird geladen …</p>
                ) : historie.length === 0 ? (
                  <p className="text-muted-foreground">Keine Einträge.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {historie.map((h) => (
                      <li key={h.id} className="flex justify-between gap-3">
                        <span>
                          {AKTION_TEXT[h.action] ?? h.action}
                          {h.actorName ? <span className="text-muted-foreground"> · {h.actorName}</span> : null}
                        </span>
                        <span className="text-muted-foreground">{formatDate(h.createdAt)}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          ) : null}
          <DialogFooter>
            {detail && darfZurueckziehen && detail.status === 'confirmed' ? (
              <Button
                variant="outline"
                onClick={() => {
                  setZurueckziehen(detail);
                  setDetail(null);
                }}
              >
                <Undo2 className="mr-1 h-4 w-4" /> Zurückziehen
              </Button>
            ) : null}
            <Button onClick={() => setDetail(null)}>Schließen</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={zurueckziehen !== null} onOpenChange={(offen) => (!offen ? setZurueckziehen(null) : undefined)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kündigung zurückziehen?</DialogTitle>
            <DialogDescription>
              {zurueckziehen?.studentName || 'Der Vertrag'} läuft dann über den{' '}
              {zurueckziehen ? formatDate(zurueckziehen.effectiveDate) : ''} hinaus weiter.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setZurueckziehen(null)} disabled={arbeitet}>
              Abbrechen
            </Button>
            <Button onClick={() => void bestaetigeZurueckziehen()} disabled={arbeitet}>
              Zurückziehen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
